import React, { useEffect, useMemo, useState } from 'react';
import { AppBar, Toolbar, Container, Box, Button, IconButton, Drawer, List, ListItem, ListItemButton, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../../img/nekrodruk-logo-dark.png';
import { useAuth } from '../../../context/AuthContext';
import axios from '../../../axios';
import ThemeToggle from '../../ThemeToggle/ThemeToggle';
import { GUIDES } from '../../Home/Guides/GuideLayout';

// Kolory jak w src/components/_brand.scss (styl ulotek) – menu wygląda tak samo jak publiczne (Home/Nav/Nav.js)
// Kolory to zmienne CSS z index.css – działają w trybie jasnym i ciemnym
const NAVY = 'var(--brand-navy)';
const ACCENT = 'var(--brand-navy)';
const LINE = 'var(--brand-line)';

const linkStyles = (active) => ({
    position: 'relative',
    fontSize: '15px',
    fontWeight: active ? 700 : 500,
    color: NAVY,
    textDecoration: 'none',
    py: 1,
    '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 2,
        height: '2px',
        background: ACCENT,
        transform: active ? 'scaleX(1)' : 'scaleX(0)',
        transformOrigin: 'left',
        transition: 'transform 0.2s ease',
    },
    '&:hover::after, &:focus-visible::after': { transform: 'scaleX(1)' },
    '&:focus-visible': { outline: 'none' },
});

const logoutButtonSx = {
    fontSize: '15px',
    px: 2.75,
    py: 1,
    whiteSpace: 'nowrap',
};

export default function NavLogged() {
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { setIsLoggedIn, isOwner, needsConsents } = useAuth();

    // match: ścieżki podstron, przy których pozycja menu ma być podświetlona
    // Na ekranie zgód panel jest jeszcze niedostępny - nie pokazujemy jego menu (zostaje „Wyloguj się”)
    const menuItems = useMemo(() => (needsConsents ? [] : (
        [
            {
                label: 'Moje konto',
                to: isOwner ? '/account' : '/accountemployee',
                match: ['/account', '/accountemployee', '/templates', '/massplaces', '/funeralplaces', '/quotes', '/definitions', '/familymembers'],
            },
            {
                label: 'Generowanie',
                to: '/personForm',
                match: ['/personForm', '/customization', '/advanced-customization', '/XMLGenerator'],
            },
            { label: 'Szarfy', to: '/sash', match: ['/sash'] },
            { label: 'Poradniki', to: '/poradniki', match: ['/poradniki', ...GUIDES.map((guide) => guide.to)] },
            { label: 'Subskrypcja', to: '/subscription', match: ['/subscription'] },
        ]
    )), [isOwner, needsConsents]);

    const currentPath = location.pathname.toLowerCase();
    const isItemActive = (item) => item.match.some((path) => {
        const normalized = path.toLowerCase();
        return currentPath === normalized || currentPath.startsWith(`${normalized}/`);
    });

    const closeMenu = () => setMenuOpen(false);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    const logoutHandler = () => {
        // Serwer unieważnia token i zapisuje wylogowanie w dzienniku administratora. Nie czekamy na odpowiedź,
        // a token podajemy jawnie, bo zaraz znika z localStorage.
        const token = localStorage.getItem('token');
        if (token) {
            axios.delete('/logout', { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        }
        setIsLoggedIn(false);
        localStorage.removeItem('token');
        navigate('/');
    };

    const handleDrawerNavigate = (to) => {
        closeMenu();
        navigate(to);
    };

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: 'var(--nav-bg)',
                    backdropFilter: 'blur(12px)',
                    borderBottom: `1px solid ${LINE}`,
                    color: NAVY,
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: { xs: 72, md: 88 } }}>
                        <Box component={NavLink} to="/" aria-label="Nekrodruk – strona główna" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <Box
                                component="img"
                                src={logo}
                                alt="Nekrodruk"
                                sx={{ height: { xs: 36, md: 44 } }}
                            />
                        </Box>
                        <Box component="nav" aria-label="Menu panelu" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: { md: 2.5, lg: 3.5 } }}>
                            {menuItems.map((item) => {
                                const active = isItemActive(item);
                                return (
                                    <Box
                                        key={item.to}
                                        component={NavLink}
                                        to={item.to}
                                        aria-current={active ? 'page' : undefined}
                                        sx={linkStyles(active)}
                                    >
                                        {item.label}
                                    </Box>
                                );
                            })}
                            <Button onClick={logoutHandler} className="app-btn-outline" sx={{ ...logoutButtonSx, ml: 1 }}>
                                Wyloguj się
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { md: 2 } }}>
                            <ThemeToggle />
                            <IconButton
                                edge="end"
                                onClick={() => setMenuOpen(true)}
                                sx={{ display: { xs: 'flex', md: 'none' }, color: NAVY }}
                                aria-label="Otwórz menu"
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="right"
                open={menuOpen}
                onClose={closeMenu}
                PaperProps={{
                    sx: {
                        width: 300,
                        maxWidth: '85vw',
                        backgroundColor: 'var(--surface)',
                        borderLeft: `3px solid ${ACCENT}`,
                        padding: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    },
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box component="img" src={logo} alt="Nekrodruk" sx={{ height: 32 }} />
                    <IconButton onClick={closeMenu} aria-label="Zamknij menu" sx={{ color: NAVY }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ borderColor: LINE }} />
                <List component="nav" aria-label="Menu panelu" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {menuItems.map((item) => {
                        const active = isItemActive(item);
                        return (
                            <ListItem key={item.to} disablePadding>
                                <ListItemButton
                                    selected={active}
                                    aria-current={active ? 'page' : undefined}
                                    onClick={() => handleDrawerNavigate(item.to)}
                                    sx={{
                                        borderRadius: 2,
                                        color: NAVY,
                                        fontSize: '16px',
                                        py: 1.25,
                                        borderLeft: '3px solid transparent',
                                        '&.Mui-selected': {
                                            fontWeight: 700,
                                            backgroundColor: 'var(--brand-soft)',
                                            borderLeftColor: ACCENT,
                                        },
                                        '&.Mui-selected:hover': { backgroundColor: 'var(--surface-2)' },
                                        '&:hover': { backgroundColor: 'var(--brand-soft)', color: ACCENT },
                                    }}
                                >
                                    {item.label}
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
                <Divider sx={{ borderColor: LINE }} />
                <Button
                    className="app-btn-outline"
                    onClick={() => {
                        closeMenu();
                        logoutHandler();
                    }}
                    sx={{ ...logoutButtonSx, width: '100%', mt: 1, py: 1.25 }}
                >
                    Wyloguj się
                </Button>
            </Drawer>
        </>
    );
}
